<template>
	<section class="activity-chat-preview">
		<div class="preview-header-bar">
			<header class="preview-header">
				<div class="chat-info">
					<div class="chat-avatar">
						<UserGroupIcon type="solid" />
					</div>
					<div class="chat-meta">
						<div class="chat-name" v-text="chatName" />
						<a
							class="open-in-tab"
							:href="openInTabHref"
							target="_blank"
							rel="noopener noreferrer"
						>
							<span>{{ $t('admin_activity_preview_open_in_new_tab') }}</span>
							<ArrowUpRightFromSquareIcon type="regular" />
						</a>
					</div>
				</div>
				<button
					type="button"
					class="close-btn"
					@click="$emit('close')"
					:aria-label="$t('admin_activity_preview_close')"
				>
					<CircleXIcon type="regular" />
				</button>
			</header>
		</div>

		<div v-if="loadError" class="preview-status preview-error">
			{{ $t('admin_activity_preview_error') }}
		</div>
		<MessageList
			v-else
			:chatId="numericChatId"
			:messages="messages"
			:readReceiptsByMessageId="readReceiptsByMessageId"
			:readOnly="true"
			:idPrefix="ID_PREFIX"
			:isListVisible="true"
			:isLoading="initialLoading"
			:isLoadingMore="loadingMore"
			:historyComplete="historyComplete"
			:allowLoadForward="!forwardHistoryComplete"
			class="preview-message-list"
			@load-backward="loadBackward"
			@load-forward="loadForward"
		/>
	</section>
</template>

<script>
import store, {
	getMessagesForBubble,
	getMembershipsForBubble,
	getCursorsForBubble,
	getStartCursorForBubble,
	getStopCursorForBubble,
} from '@/store'
import { fetchHistory, fetchMyMembershipForChat } from '@/api/chat'
import { useCenteredMessage } from '@/composables/useCenteredMessage'
import { MESSAGE_PAGE_SIZE } from '@/helpers/constants'
import MessageList from '@/components/Chat/MessageList'
import { UserGroupIcon, ArrowUpRightFromSquareIcon, CircleXIcon } from '@/icons'

const ID_PREFIX = 'activity-preview-'

export default {
	name: 'ActivityChatPreview',

	components: {
		MessageList,
		UserGroupIcon,
		ArrowUpRightFromSquareIcon,
		CircleXIcon,
	},

	props: {
		chatId: { type: [Number, String], required: true },
		messageId: { type: [Number, String], required: true },
		chatName: { type: String, default: '' },
	},

	emits: ['close'],

	setup() {
		const { load, scrollIntoView } = useCenteredMessage()
		return { loadCentered: load, scrollCenteredIntoView: scrollIntoView }
	},

	data() {
		return {
			ID_PREFIX,
			initialLoading: true,
			loadError: false,
			historyComplete: false,
			forwardHistoryComplete: false,
			loadingMore: false,
		}
	},

	computed: {
		numericChatId() {
			return Number(this.chatId)
		},

		numericMessageId() {
			return Number(this.messageId)
		},

		messages() {
			// During the initial centered fetch, return empty so the loading
			// skeleton paints over a clean list instead of stacking on top of
			// the previous chat's messages still sitting in the global store.
			if (this.initialLoading) return []
			const { start, stop } = getCursorsForBubble(this.numericChatId)
			const all = getMessagesForBubble(this.numericChatId)
			if (start == null || stop == null) return all
			return all.filter((m) => !m.id || (m.id >= start && m.id <= stop))
		},

		readReceiptsByMessageId() {
			// Mirrors ChatPanel.calculateReadReceipts: group each member's
			// last-read `mark` to bucket users under the message they've read
			// up to. Marks pointing at messages outside the loaded window get
			// folded onto the nearest earlier loaded message.
			if (this.initialLoading) return {}
			const myId = store.auth.user?.id
			const memberships = getMembershipsForBubble(this.numericChatId)
			const messageIds = this.messages
				.map((m) => m.id)
				.filter(Boolean)
				.sort((a, b) => a - b)
			if (!messageIds.length || !memberships.length) return {}

			const userIdsByMark = memberships.reduce((acc, memb) => {
				if (memb.user_id === myId) return acc
				acc[memb.mark] = (acc[memb.mark] || []).concat(memb.user_id)
				return acc
			}, {})

			const receipts = messageIds.reduce((acc, msgId) => {
				acc[msgId] = userIdsByMark[msgId] || []
				return acc
			}, {})

			Object.entries(userIdsByMark).forEach(([markRaw, userIds]) => {
				const mark = Number(markRaw)
				if (receipts[mark]) return
				const nextIdx = messageIds.findIndex((id) => id > mark)
				let attachIdx
				if (nextIdx === -1) attachIdx = messageIds.length - 1
				else if (nextIdx > 0) attachIdx = nextIdx - 1
				else return // mark predates our window — drop
				const attachTo = messageIds[attachIdx]
				receipts[attachTo] = receipts[attachTo].concat(userIds)
			})

			return receipts
		},

		openInTabHref() {
			return this.$router.resolve({
				name: 'chat',
				params: { chat: 'chat', id: String(this.numericChatId) },
				query: { centered: this.numericMessageId },
			}).href
		},
	},

	async mounted() {
		// Kick off the membership fetch in parallel — it populates the
		// store with all chat memberships (including each member's `mark`)
		// which feeds the read-receipt computed. Best-effort: if the admin
		// isn't in the chat, this 404s and we simply show no receipts.
		fetchMyMembershipForChat(this.numericChatId).catch(() => {})

		const result = await this.loadCentered(
			this.numericChatId,
			this.numericMessageId
		)
		this.initialLoading = false
		if (!result) {
			this.loadError = true
			return
		}
		if (result.older.messages.length < MESSAGE_PAGE_SIZE) {
			this.historyComplete = true
		}
		if (!result.newer.messages.length) {
			this.forwardHistoryComplete = true
		}
		await this.$nextTick()
		this.scrollCenteredIntoView(this.numericMessageId, ID_PREFIX)
	},

	methods: {
		async loadBackward() {
			// initialLoading guard: MessageList's IntersectionRoot observer
			// can fire @load-backward / @load-forward before the centered
			// fetch resolves. Without the guard, cursors are undefined and
			// fetchHistory falls back to "latest 50" which clobbers the
			// centered window.
			if (this.initialLoading || this.historyComplete || this.loadingMore)
				return
			this.loadingMore = true
			try {
				const latest = getStartCursorForBubble(this.numericChatId)
				const { messages } = await fetchHistory({
					bubble_id: this.numericChatId,
					latest,
				})
				if (messages.length < MESSAGE_PAGE_SIZE) {
					this.historyComplete = true
				}
			} catch (e) {
				console.error(e)
			} finally {
				this.loadingMore = false
			}
		},

		async loadForward() {
			if (
				this.initialLoading ||
				this.forwardHistoryComplete ||
				this.loadingMore
			)
				return
			this.loadingMore = true
			try {
				const oldest = getStopCursorForBubble(this.numericChatId)
				const { messages } = await fetchHistory({
					bubble_id: this.numericChatId,
					oldest,
				})
				if (messages.length < MESSAGE_PAGE_SIZE) {
					this.forwardHistoryComplete = true
				}
			} catch (e) {
				console.error(e)
			} finally {
				this.loadingMore = false
			}
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.activity-chat-preview {
	display: flex;
	flex-direction: column;
	height: 100%;
	background: var(--neutral-0);
	border-left: 1px solid var(--neutral-200);
}

.preview-header-bar {
	flex-shrink: 0;
	position: relative;
	// Paint shadow above the sibling message list when content scrolls
	// up beneath the bar. MessageList isolates its own z-indexes, so a
	// z-index of 1 here is enough to win.
	z-index: 1;
	padding: 12px 16px;
	background: var(--neutral-0);
	box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.1), 0 1px 3px 0 rgba(0, 0, 0, 0.1);
}

.preview-header {
	display: flex;
	align-items: center;
	gap: 16px;
	padding: 24px;
	background: #f9fafb;
	border-radius: 10px;
}

.chat-info {
	display: flex;
	align-items: center;
	gap: 16px;
	flex: 1;
	min-width: 0;
}

.chat-avatar {
	@include flex-center;
	flex-shrink: 0;
	width: 48px;
	height: 48px;
	border-radius: 6px;
	background: var(--neutral-0);
	color: var(--neutral-500);
	font-size: 24px;
}

.chat-meta {
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
}

.chat-name {
	font-family: Avenir-Roman;
	font-size: 18px;
	color: var(--neutral-800);
	letter-spacing: -0.44px;
	@include clamp(1);
}

.open-in-tab {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	font-family: Avenir-Roman;
	font-size: 14px;
	color: var(--blue-400);
	text-decoration: underline;
	letter-spacing: -0.15px;

	.pronto-icon {
		font-size: 12px;
	}

	&:hover {
		opacity: 0.85;
	}
}

.close-btn {
	@include flex-center;
	background: none;
	border: none;
	padding: 0;
	color: var(--blue-400);
	cursor: pointer;
	flex-shrink: 0;

	.pronto-icon {
		font-size: 20px;
	}

	&:hover {
		opacity: 0.85;
	}
}

.preview-message-list {
	flex-grow: 1;
}

.preview-status {
	@include flex-center;
	flex-grow: 1;
	padding: 32px;
	font-family: Avenir-Roman;
	font-size: 14px;
	color: var(--neutral-600);
}

.preview-error {
	color: var(--red-400);
}
</style>
